import { dayjs } from '@2wunder/klarx-tool'
import { Descriptions, List as AntList, Space } from 'antd'
import { CollectionProps } from 'app/components/Core/convention'
import LineItemBaseItem from 'app/components/Core/LineItem/Item/Base'
import KList from 'app/components/Modules/List/KList'
import CheckboxRow from 'app/components/UI-Elements/DataEntry/CheckbowRow'
import { RecordType } from 'app/constants/GlobalTypes'
import { ToolBox } from 'app/lib/hooks/toolBox'
import * as React from 'react'
import { ComponentProps } from 'react'

import FreeSubItemList from './FreeSubItemList'

type Props = ComponentProps<typeof KList> & Omit<ListItemProps, 'item'>

const List: React.FC<CollectionProps<IMachineGroup, Props>> = ({
  collection = [],
  props: { showDates, showStatus, showCheckupDays, onCheck, selectedItems, ...others }
}) => {
  const { tAttribute } = ToolBox.useString()

  const getTitle = (date: string) =>
    `${tAttribute({
      recordType: RecordType.machine_group,
      attribute: 'return_date'
    })}: ${dayjs(date).calendar()}`

  const listItemProps: Omit<ListItemProps, 'item'> = {
    showDates,
    showStatus,
    showCheckupDays,
    onCheck,
    selectedItems
  }

  return (
    <KList
      split={false}
      itemLayout="vertical"
      dataSource={collection.sort((a, b) => dayjs(a.date).diff(dayjs(b.date)))}
      renderItem={(item: IMachineGroup) => (
        <AntList.Item>
          <Descriptions layout="vertical" bordered size="small" column={{ xs: 24 }}>
            <Descriptions.Item label={getTitle(item.date)}>
              <KList<ILineItem>
                dataSource={item.lineItems}
                renderItem={(item: ILineItem) => (
                  <React.Fragment>
                    <AntList.Item key={item.id}>
                      <ListItem item={item} {...listItemProps} />
                    </AntList.Item>
                    <SubItemList subItems={item.subItems} listItemProps={listItemProps} />
                  </React.Fragment>
                )}
                itemLayout="vertical"
              />
            </Descriptions.Item>
          </Descriptions>
        </AntList.Item>
      )}
      {...others}
    />
  )
}

type ListItemProps = {
  item: ILineItem
  showDates?: boolean
  showStatus?: boolean
  showCheckupDays?: boolean
  onCheck?: (checked: boolean, lineItem: ILineItem) => void
  selectedItems?: ILineItem[]
}

function ListItem({
  item,
  showDates,
  showStatus,
  showCheckupDays,
  onCheck,
  selectedItems
}: ListItemProps) {
  const isChecked = (item: ILineItem) => !!selectedItems?.find(({ id }) => id === item.id)

  return (
    <CheckboxRow
      align="start"
      disabled={item.vacant}
      onCheck={(val) => onCheck(val, item)}
      checked={isChecked(item)}
      showCheckbox={Boolean(onCheck)}>
      <Space direction="vertical">
        <LineItemBaseItem member={item} props={{ showDates, showStatus, showCheckupDays }} />
        <FreeSubItemList lineItem={item} />
      </Space>
    </CheckboxRow>
  )
}

type SubItemListProps = {
  subItems: ISubItem[]
  level?: number
  listItemProps?: Omit<ListItemProps, 'item'>
}

function SubItemList({ subItems, level = 1, listItemProps = {} }: SubItemListProps) {
  return (
    <React.Fragment>
      {(subItems || [])
        .filter((subItem) => !subItem.freeAttachment)
        .map((subItem) => (
          <React.Fragment key={subItem.id}>
            <AntList.Item style={{ paddingLeft: level * 20 }}>
              <ListItem item={subItem} {...listItemProps} />
            </AntList.Item>
            {Boolean(subItem.subItems?.length) && (
              <SubItemList
                subItems={subItem.subItems}
                level={level + 1}
                listItemProps={listItemProps}
              />
            )}
          </React.Fragment>
        ))}
    </React.Fragment>
  )
}

export default List
